import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { reportLayoutTokens } from "./tokens";

export type ReportTableColumn<Row> = {
  id: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  // Optional width hint, e.g. 120 or "12%"
  width?: string | number;
  cell: (row: Row, rowIndex: number) => ReactNode;
};

export type ReportTableTreeConfig<Row> = {
  getKey: (row: Row) => string;
  getParentKey: (row: Row) => string | null;
  isExpandable: (row: Row) => boolean;
  getDepth: (row: Row) => number;
  startCollapsed?: boolean;
  expandColumnId?: string;
};

export type ReportDataTableProps<Row> = {
  columns: ReportTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row, index: number) => string | number;
  isLoading?: boolean;
  // Shown when not loading and rows are empty
  emptyMessage?: ReactNode;
  // Default: "small"
  size?: "small" | "medium";
  // Header row background and text color overrides
  tableHeaderBackground?: string;
  tableHeaderTextColor?: string;
  // Row rendered before data rows (e.g. opening balance)
  leadingRow?: ReactNode;
  footer?: ReactNode;
  tree?: ReportTableTreeConfig<Row>;
};

// Config-driven report table with custom cell renderers and shared header style.
function ReportDataTable<Row>({
  columns,
  rows,
  getRowId,
  isLoading,
  emptyMessage,
  size = "small",
  tableHeaderBackground = reportLayoutTokens.tableHeaderBg,
  tableHeaderTextColor = reportLayoutTokens.tableHeaderText,
  leadingRow,
  footer,
  tree,
}: ReportDataTableProps<Row>) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const startCollapsed = tree?.startCollapsed ?? false;

  useEffect(() => {
    if (!tree) {
      return;
    }
    if (!startCollapsed) {
      setCollapsed(new Set());
      return;
    }
    const keys = new Set<string>();
    rows.forEach((row) => {
      if (tree.isExpandable(row)) keys.add(tree.getKey(row));
    });
    setCollapsed(keys);
    // Seed collapse from the latest rows only; tree callbacks stay identity-stable
    // when the parent memoizes the config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, startCollapsed]);

  useEffect(() => {
    if (!tree) {
      return;
    }
    const expandAll = () => setCollapsed(new Set());
    window.addEventListener("beforeprint", expandAll);
    return () => window.removeEventListener("beforeprint", expandAll);
  }, [tree]);

  const visibleRows = useMemo(() => {
    if (!tree) return rows;
    const byKey = new Map(rows.map((row) => [tree.getKey(row), row]));
    return rows.filter((row) => {
      let parent = tree.getParentKey(row);
      while (parent) {
        if (collapsed.has(parent)) return false;
        parent = byKey.get(parent)
          ? tree.getParentKey(byKey.get(parent) as Row)
          : null;
      }
      return true;
    });
  }, [collapsed, rows, tree]);

  const toggleRow = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandColumnId = tree?.expandColumnId ?? columns[0]?.id;

  return (
    <TableContainer
      sx={{
        maxWidth: "100%",
        borderRadius: 0,
      }}
    >
      <Table size={size} stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                bgcolor: tableHeaderBackground,
                color: tableHeaderTextColor,
                fontWeight: 600,
                fontSize: "0.75rem",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                borderBottom: "1px solid #EAECF0",
                whiteSpace: "nowrap",
                py: 1.25,
              },
            }}
          >
            {columns.map((col) => (
              <TableCell
                key={col.id}
                align={col.align ?? "left"}
                width={col.width}
                sx={col.align === "right" ? { pr: 2 } : undefined}
              >
                {col.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                <CircularProgress size={32} />
              </TableCell>
            </TableRow>
          ) : (
            <>
              {leadingRow}
              {!visibleRows || visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    {emptyMessage != null ? (
                      emptyMessage
                    ) : (
                      <Typography color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, index) => {
                  const depth = tree ? tree.getDepth(row) : 0;
                  const expandable = !!tree && tree.isExpandable(row);
                  const rowKey = tree ? tree.getKey(row) : String(getRowId(row, index));
                  const isCollapsed = expandable && collapsed.has(rowKey);

                  return (
                    <TableRow
                      key={getRowId(row, index)}
                      sx={{
                        bgcolor: index % 2 === 1 ? "#FCFCFC" : undefined,
                        "&:last-of-type td": { borderBottom: 0 },
                        "& td": {
                          borderColor: "#F0ECE8",
                          fontSize: "0.875rem",
                          color: "#101828",
                          py: 1.5,
                        },
                      }}
                    >
                      {columns.map((col) => {
                        const isExpandCol = !!tree && col.id === expandColumnId;
                        return (
                          <TableCell
                            key={col.id}
                            align={col.align ?? "left"}
                            sx={
                              isExpandCol
                                ? { pl: 2 + depth * 3 }
                                : undefined
                            }
                          >
                            {isExpandCol ? (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {expandable ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRow(rowKey)}
                                    sx={{ p: 0.25, ml: -0.75 }}
                                    aria-label={
                                      isCollapsed ? "expand row" : "collapse row"
                                    }
                                  >
                                    {isCollapsed ? (
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    ) : (
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                ) : (
                                  <Box width={20} flexShrink={0} />
                                )}
                                <Box sx={{ minWidth: 0 }}>{col.cell(row, index)}</Box>
                              </Box>
                            ) : (
                              col.cell(row, index)
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </>
          )}
        </TableBody>
        {footer && <TableFooter>{footer}</TableFooter>}
      </Table>
    </TableContainer>
  );
}

export default ReportDataTable;
